/**
 * The shell's own Temporary Permits lookup.
 *
 * One caller: resolving the id in `/temporary-permits/requests/<id>`, `/temporary-permits/cases/<id>`,
 * `/temporary-permits/guarantees/<id>` and `/temporary-permits/assets/<id>` to the record's own
 * reference, so the breadcrumb reads "Home › Transport › Requests ›
 * TRF-2026-0212" rather than the generic "Details" crumb.
 *
 * Alongside `companies.ts` and `users.ts` and for the same reason: the
 * entity-label registry lives in the shell, and temporary-permits-ui does not depend on it
 * (nor should the shell import temporary-permits-ui, which loads lazily per company).
 */
import apiClient from './client';

export type TemporaryPermitsEntityType = 'request' | 'case' | 'guarantee' | 'asset';

interface TemporaryPermitsLabelFields {
  number?: string;
  reference?: string;
  serial?: string | null;
  assetCode?: string | null;
}

const PATHS: Record<TemporaryPermitsEntityType, string> = {
  request: 'requests',
  case: 'cases',
  guarantee: 'guarantees',
  asset: 'assets',
};

/** What each record calls itself on its own page header. */
const labelOf = (type: TemporaryPermitsEntityType, record: TemporaryPermitsLabelFields): string | null => {
  if (type === 'request' || type === 'case') return record.number ?? null;
  if (type === 'guarantee') return record.reference ?? null;
  // The asset page's own title: the serial when there is one, else the code.
  if (record.serial) return `S/N ${record.serial}`;
  return record.assetCode ?? null;
};

export const getTemporaryPermitsEntityLabel = async (
  type: TemporaryPermitsEntityType,
  id: string
): Promise<string | null> => {
  const response = await apiClient.get<{ success: boolean; data: TemporaryPermitsLabelFields }>(
    `/api/temporary-permits/${PATHS[type]}/${encodeURIComponent(id)}`
  );
  return labelOf(type, response.data.data);
};
